/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectHorsesBehindGoal, selectHorsesStarted, selectHurdles, selectRound } from '~/app';
import Turfmaster from '~/server/src/tmmodel';
import { transformAbsX, transformAbsY, vh, vw } from '~/utils';

import { filterVisibleHurdles, hurdleRenderFields } from './hurdlePoi';

const hurdleSize = 60;

const HurdleView = () => {
  const round = useSelector(selectRound);
  const hurdles = useSelector(selectHurdles);
  const horsesStarted = useSelector(selectHorsesStarted);
  const horsesBehindGoal = useSelector(selectHorsesBehindGoal);

  const hurdlePaths: string[] = useMemo(() => {
    if (!Turfmaster.hurdleRounds.includes(round)) return [];

    const visibility = { round, horsesStarted, horsesBehindGoal };
    const visible = filterVisibleHurdles(hurdles, visibility);
    const hurdlePaths: string[] = [];

    visible.forEach((hurdle, hurdleIndex) => {
      let d = '';
      const fields = hurdleRenderFields(hurdle, hurdleIndex, visible, visibility);

      fields.forEach((field, index) => {
        d += `${index === 0 ? 'M' : 'L'}${transformAbsX(field.x)} ${transformAbsY(field.y)}`;
      });
      hurdlePaths.push(d);
    });

    return hurdlePaths;
  }, [horsesBehindGoal, horsesStarted, hurdles, round]);

  return (
    <svg
      className="pointer-events-none absolute h-full w-full overflow-hidden"
      viewBox={`0 0 ${vw} ${vh}`}
    >
      <defs>
        <pattern
          id="fancy-rectangles"
          x="0"
          y="0"
          width="60"
          height="48"
          patternUnits="userSpaceOnUse"
        >
          <rect fill="#125214" width="60" height="48" />
          <path
            fill="transparent"
            stroke="#d3d3d3"
            opacity={0.3}
            d="M6 12h6v12H6V12zm12 0h6v12h-6V12zm6-12h6v12h-6V0zM12 0h6v12h-6V0zm0 24h6v12h-6V24zM0 0h6v12H0V0zm6 36h6v12H6V36zm12 0h6v12h-6V36zm12-12h6v12h-6V24zM42 0h6v12h-6V0zm-6 12h6v12h-6V12zm12 0h6v12h-6V12zM36 36h6v12h-6V36zm12 0h6v12h-6V36zm-6-12h6v12h-6V24zm12 0h6v12h-6V24z"
          />
        </pattern>
      </defs>
      {hurdlePaths.map((hurdlePath, index) => (
        <path
          key={index}
          d={hurdlePath}
          className="animate-bounce-in origin-center"
          style={{ animationDelay: `${index * 200}ms` }}
          stroke="url(#fancy-rectangles)"
          strokeWidth={hurdleSize}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
};

export default memo(HurdleView);
